import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './DiagramViewer.css';
import MermaidDiagram from './MermaidDiagram';
import CodeBlock from './CodeBlock';
import { diagramCategories, diagramFiles } from '../data/diagrams';
import { parseMarkdownWithDiagrams, markdownToHtml } from '../utils/markdownParser';
import { loadDiagramContent } from '../services/diagramLoader';
import { filterSensitiveContent } from '../utils/contentFilter';

function DiagramViewer({ onLogout, currentUser, currentRole }) {
  const { diagramId } = useParams();
  const navigate = useNavigate();
  const [diagramTitles, setDiagramTitles] = useState({});
  const [diagramContent, setDiagramContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());
  const loadingRef = useRef(null); // Referencia para evitar cargas duplicadas

  // Filtrar diagramas según permisos del rol
  const allowedDiagrams = useMemo(() => {
    if (!currentRole) return [];
    if (currentRole.isSuperAdmin || currentRole.permissions.modules.includes('*')) {
      return diagramFiles;
    }
    return diagramFiles.filter(diagram =>
      currentRole.permissions.modules.includes(diagram.id)
    );
  }, [currentRole]);

  // Filtrar categorías según permisos
  const allowedCategories = useMemo(() => {
    if (!currentRole) return [];

    return diagramCategories.map(category => {
      if (category.subcategories) {
        const filteredSubcats = category.subcategories.map(subcat => ({
          ...subcat,
          diagrams: subcat.diagrams.filter(diagram =>
            currentRole.isSuperAdmin ||
            currentRole.permissions.modules.includes('*') ||
            currentRole.permissions.modules.includes(diagram.id)
          )
        })).filter(subcat => subcat.diagrams.length > 0);

        return filteredSubcats.length > 0 ? { ...category, subcategories: filteredSubcats } : null;
      } else if (category.diagrams) {
        const filteredDiagrams = category.diagrams.filter(diagram =>
          currentRole.isSuperAdmin ||
          currentRole.permissions.modules.includes('*') ||
          currentRole.permissions.modules.includes(diagram.id)
        );

        return filteredDiagrams.length > 0 ? { ...category, diagrams: filteredDiagrams } : null;
      }
      return null;
    }).filter(Boolean);
  }, [currentRole]);

  // Construir títulos desde la configuración
  useEffect(() => {
    const titles = {};
    allowedDiagrams.forEach(diagram => {
      titles[diagram.id] = diagram.title || diagram.id;
    });
    setDiagramTitles(titles);
  }, [allowedDiagrams]);

  // Expandir automáticamente la categoría/subcategoría del diagrama actual
  useEffect(() => {
    if (diagramId && allowedCategories.length > 0) {
      // Buscar la categoría y subcategoría del diagrama actual
      for (const category of allowedCategories) {
        if (category.subcategories) {
          for (const subcategory of category.subcategories) {
            const found = subcategory.diagrams.find(d => d.id === diagramId);
            if (found) {
              setExpandedCategories(new Set([category.id]));
              setExpandedSubcategories(new Set([subcategory.id]));
              return;
            }
          }
        } else if (category.diagrams) {
          const found = category.diagrams.find(d => d.id === diagramId);
          if (found) {
            setExpandedCategories(new Set([category.id]));
            return;
          }
        }
      }
    }
  }, [diagramId, allowedCategories]);

  // Función para cargar un diagrama
  const loadDiagram = useCallback(async (diagram) => {
    // Evitar cargas duplicadas
    if (loadingRef.current === diagram.id) {
      return;
    }

    loadingRef.current = diagram.id;
    setLoading(true);
    setError(null);

    try {
      // Cargar el contenido usando el servicio seguro (archivos bundleados en src/)
      let markdownText = await loadDiagramContent(diagram.id);

      // Filtrar contenido sensible basado en permisos del usuario
      markdownText = filterSensitiveContent(markdownText, currentUser);

      const sections = parseMarkdownWithDiagrams(markdownText);

      setDiagramContent(sections);
    } catch (err) {
      setError('Error al cargar el diagrama: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
      loadingRef.current = null;
    }
  }, [currentUser]);

  // Cargar el diagrama cuando cambia el ID en la URL
  useEffect(() => {
    if (diagramId) {
      const diagram = allowedDiagrams.find(d => d.id === diagramId);
      if (diagram) {
        loadDiagram(diagram);
      } else {
        setError('No tienes permisos para ver este diagrama');
      }
    }
  }, [diagramId, allowedDiagrams, loadDiagram]);

  const handleDiagramClick = (diagramId) => {
    navigate(`/docs/${diagramId}`);
    setIsMobileMenuOpen(false); // Cerrar menú siempre al hacer click
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Bloquear scroll del body cuando el menú mobile está abierto
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isMobileMenuOpen]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const toggleSubcategory = (subcategoryId) => {
    setExpandedSubcategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subcategoryId)) {
        newSet.delete(subcategoryId);
      } else {
        newSet.add(subcategoryId);
      }
      return newSet;
    });
  };

  return (
    <div className="diagram-viewer">
      <header className="viewer-header">
        <div className="header-content">
          <div className="header-left">
            <button
              className="mobile-menu-button"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isMobileMenuOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </>
                ) : (
                  <>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </>
                )}
              </svg>
            </button>
            <h1>Documentación de Hi-G</h1>
          </div>
          <div className="header-actions">
            {(currentRole?.isSuperAdmin || currentRole?.isAdmin) && (
              <button onClick={() => navigate('/admin')} className="admin-button" aria-label="Panel de administración">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
                <span>Admin</span>
              </button>
            )}
            <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle theme">
              {theme === 'light' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                  <line x1="1" y1="12" x2="3" y2="12"></line>
                  <line x1="21" y1="12" x2="23" y2="12"></line>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </svg>
              )}
            </button>
            <button onClick={onLogout} className="logout-button" aria-label="Cerrar sesión">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'none' }}>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </header>

      <div className="viewer-container">
        <aside className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <button
            className={`home-button ${!diagramId ? 'active' : ''}`}
            onClick={() => {
              navigate('/docs');
              setIsMobileMenuOpen(false);
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            <span>Inicio</span>
          </button>
          {allowedCategories.length === 0 ? (
            <p style={{ padding: '1rem', color: 'var(--text-muted)' }}>
              No tienes permisos para ver ningún diagrama. Contacta al administrador.
            </p>
          ) : (
            <nav className="diagram-list">
              {allowedCategories.map((category) => (
                <div key={category.id} className="category-group">
                  <button
                    className={`category-header ${expandedCategories.has(category.id) ? 'expanded' : ''}`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <svg className="chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                    <span>{category.title}</span>
                  </button>

                  {expandedCategories.has(category.id) && (
                    <div className="category-content">
                      {category.subcategories && category.subcategories.map((subcategory) => (
                        <div key={subcategory.id} className="subcategory-group">
                          <button
                            className={`subcategory-header ${expandedSubcategories.has(subcategory.id) ? 'expanded' : ''}`}
                            onClick={() => toggleSubcategory(subcategory.id)}
                          >
                            <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                            <span>{subcategory.title}</span>
                          </button>

                          {expandedSubcategories.has(subcategory.id) && (
                            <div className="subcategory-content">
                              {subcategory.diagrams.map((diagram) => (
                                <button
                                  key={diagram.id}
                                  className={`diagram-item ${diagramId === diagram.id ? 'active' : ''}`}
                                  onClick={() => handleDiagramClick(diagram.id)}
                                >
                                  <h3>{diagramTitles[diagram.id] || 'Cargando...'}</h3>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      {category.diagrams && category.diagrams.map((diagram) => (
                        <button
                          key={diagram.id}
                          className={`diagram-item ${diagramId === diagram.id ? 'active' : ''}`}
                          onClick={() => handleDiagramClick(diagram.id)}
                        >
                          <h3>{diagramTitles[diagram.id] || 'Cargando...'}</h3>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          )}
        </aside>

        {isMobileMenuOpen && (
          <div className="mobile-overlay" onClick={toggleMobileMenu}></div>
        )}

        <main className="content">
          {!diagramId && !loading && (
            <div className="home-view">
              <h1>Bienvenido a la Documentación de Hi-G</h1>
              <p className="home-description">
                Explora todos los diagramas y documentación técnica de la aplicación.
              </p>

              <div className="home-categories">
                {allowedCategories.map((category) => (
                  <div key={category.id} className="home-category-card">
                    <h2>{category.title}</h2>

                    {category.subcategories && category.subcategories.map((subcategory) => (
                      <div key={subcategory.id} className="home-subcategory">
                        <h3>{subcategory.title}</h3>
                        <div className="home-diagrams-grid">
                          {subcategory.diagrams.map((diagram) => (
                            <button
                              key={diagram.id}
                              className="home-diagram-card"
                              onClick={() => handleDiagramClick(diagram.id)}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                              </svg>
                              <span>{diagramTitles[diagram.id] || diagram.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    {category.diagrams && (
                      <div className="home-diagrams-grid">
                        {category.diagrams.map((diagram) => (
                          <button
                            key={diagram.id}
                            className="home-diagram-card"
                            onClick={() => handleDiagramClick(diagram.id)}
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            <span>{diagramTitles[diagram.id] || diagram.title}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {diagramId && loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Cargando diagrama...</p>
            </div>
          )}

          {diagramId && error && (
            <div className="error-box">
              <h3>Error</h3>
              <p>{error}</p>
            </div>
          )}

          {diagramId && !loading && !error && diagramContent && (
            <div className="diagram-content">
              {diagramContent.map((section, index) => (
                <React.Fragment key={index}>
                  {section.type === 'text' && (
                    <div
                      className="text-content"
                      dangerouslySetInnerHTML={{ __html: markdownToHtml(section.content) }}
                    />
                  )}
                  {section.type === 'mermaid' && (
                    <MermaidDiagram chart={section.content} />
                  )}
                  {section.type === 'code' && (
                    <CodeBlock code={section.content} language={section.language} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default DiagramViewer;
